import { ROUTES } from '@/constants'
import { FullscriptEmbedProvider } from '@/integrations/fullscript'
import { PageLoader } from '@/components/ui/PageLoader'
import { usePermissions } from '@/services/permissions'
import { useProfile } from '@/services/profile'
import { useAssignedOpenTaskCount } from '@/services/task'
import { setSidebarMobileOpen, useAppDispatch } from '@/store'
import {
  Menu as MenuIcon,
  AccountCircle as ProfileIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material'
import { AppBar, Box, IconButton, Toolbar, useMediaQuery, useTheme } from '@mui/material'
import { Outlet } from 'react-router-dom'
import { CLINICIAN_NAV_ITEMS } from './clinician-nav-items'
import type { SidebarNavItem } from './Sidebar'
import { Sidebar } from './Sidebar'
import { useBrand } from '@/branding'

/**
 * Items name the permission that makes them visible; the list is filtered once per render.
 * Gating follows the backend module by module — entries without a key are the modules not yet
 * gated, and are shown to every clinician-portal user exactly as before.
 */

const CLINICIAN_FOOTER_ITEMS: SidebarNavItem[] = [
  { to: ROUTES.APP.PROFILE, icon: <ProfileIcon />, label: 'Profile' },
  { to: ROUTES.APP.SETTINGS, icon: <SettingsIcon />, label: 'Settings' },
]

export const DashboardLayout = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const dispatch = useAppDispatch()
  const brand = useBrand()
  const { data: profile } = useProfile()
  // Nav badge — only fetched once the profile resolves so the call carries a valid session.
  const { data: assignedOpenCount = 0 } = useAssignedOpenTaskCount(Boolean(profile))

  const { canAny, isLoading: permissionsLoading } = usePermissions()

  const baseNavItems = CLINICIAN_NAV_ITEMS.filter(
    (item) =>
      !item.permission ||
      canAny(...(Array.isArray(item.permission) ? item.permission : [item.permission])),
  )

  // Decide the menu once the permissions are known, so items never flash in and out.
  if (permissionsLoading) return <PageLoader />

  const navItems = baseNavItems.map((item) =>
    item.to === ROUTES.APP.TASKS
      ? {
          ...item,
          badge: assignedOpenCount,
          badgeTooltip: `${assignedOpenCount} open ${assignedOpenCount === 1 ? 'task' : 'tasks'} assigned to you`,
        }
      : item,
  )

  return (
    <Box
      sx={{ display: 'flex', height: '100vh', overflow: 'hidden', bgcolor: 'background.default' }}
    >
      {/* Mobile AppBar */}
      {isMobile && (
        <AppBar
          position="fixed"
          elevation={0}
          sx={{ bgcolor: 'background.paper', borderBottom: `1px solid ${theme.palette.divider}` }}
        >
          <Toolbar>
            <IconButton edge="start" onClick={() => dispatch(setSidebarMobileOpen(true))}>
              <MenuIcon />
            </IconButton>
            <Box
              component="img"
              src={brand.logo}
              alt={brand.name}
              sx={{ height: 28, width: 'auto', ml: 1 }}
            />
          </Toolbar>
        </AppBar>
      )}

      {/* Sidebar */}
      <Sidebar
        navItems={navItems}
        footerItems={CLINICIAN_FOOTER_ITEMS}
        profileItemTo={ROUTES.APP.PROFILE}
        avatarUrl={profile?.avatar}
      />

      {/* Main content — wrapped in the Fullscript embed provider so any
          clinician route (lab orders wizard, results tab, etc.) can mount
          the Fullscript SDK without re-initialising the script. */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          pt: isMobile ? 8 : 3,
          pb: 3,
          px: { xs: 2, sm: 3 },
        }}
      >
        <FullscriptEmbedProvider>
          <Outlet />
        </FullscriptEmbedProvider>
      </Box>
    </Box>
  )
}
